import { forwardRef } from 'react';

import type { CanvasRef } from '../../core/components/canvas/Canvas.js';
import { RectDisplay } from '../components/displays/RectDisplay.js';
import { useXYChartData } from '../hooks/useXYChartData.js';
import { useXYChartScales } from '../hooks/useXYChartScales.js';
import type {
  RectDatapointInternal,
  XYChartSeriesWithDatapoints,
} from '../types/xy-chart-internals.js';
import { XYChartCanvasPlot } from './XYChartCanvasPlot.js';
import { XYChartShiftedDisplay } from './XYChartShiftedDisplay.js';

interface XYChartGenericCanvasProps {
  seriesWithDatapoints: XYChartSeriesWithDatapoints[];
  rectFilterCallback?: (d: RectDatapointInternal) => boolean;
}

export const XYChartGenericCanvasLayout = forwardRef<
  CanvasRef[],
  XYChartGenericCanvasProps
>(
  (
    { seriesWithDatapoints, rectFilterCallback }: XYChartGenericCanvasProps,
    canvasRefs,
  ) => {
    const axisIdToScales = useXYChartScales();
    const { seriesToMetadata } = useXYChartData();

    const handleRef = (el: CanvasRef | null, index: number) => {
      if (
        canvasRefs &&
        typeof canvasRefs !== 'function' &&
        canvasRefs.current &&
        el
      ) {
        canvasRefs.current[index] = el;
      }
    };

    return (
      <>
        <XYChartCanvasPlot ref={(el) => handleRef(el, 0)}>
          <RectDisplay
            axisIdToScales={axisIdToScales}
            series={seriesWithDatapoints.filter(
              (serie) => serie.type === 'rect',
            )}
            rectFilterCallback={rectFilterCallback}
          />
        </XYChartCanvasPlot>
        <XYChartCanvasPlot ref={(el) => handleRef(el, 1)} shifted>
          {seriesWithDatapoints.map((series) => (
            <XYChartShiftedDisplay
              key={series.id}
              series={series}
              axisIdToScales={axisIdToScales}
              seriesToMetadata={seriesToMetadata}
            />
          ))}
        </XYChartCanvasPlot>
      </>
    );
  },
);
